import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Bot, Lock, LogOut, MessagesSquare, ShieldCheck, User } from "lucide-react";
import { QuoteSection } from "@/components/portfolio/QuoteSection";
import { ProjectTracker } from "@/components/portfolio/ProjectTracker";
import { PaymentsSection } from "@/components/portfolio/PaymentsSection";
import { PageLoader } from "@/components/portfolio/PageLoader";
import { Stagger, StaggerItem } from "@/components/portfolio/Reveal";
import { API } from "@/lib/api";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return "";
  }
};

export default function AccountPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const processed = useRef(false);

  useEffect(() => {
    if (location.hash?.includes("session_id=")) {
      if (processed.current) return;
      processed.current = true;
      const sessionId = new URLSearchParams(location.hash.slice(1)).get("session_id");
      fetch(`${API}/auth/google/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId }),
      })
        .then((r) => {
          if (!r.ok) throw new Error();
          return r.json();
        })
        .then((u) => {
          window.history.replaceState(null, "", "/conta");
          if (u.role === "admin") {
            navigate("/admin", { replace: true });
          } else {
            setUser(u);
          }
        })
        .catch(() => navigate("/entrar", { replace: true }));
      return;
    }
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((u) => {
        if (u.role === "admin") {
          navigate("/admin", { replace: true });
        } else {
          setUser(u);
        }
      })
      .catch(() => navigate("/entrar", { replace: true }));
  }, [location.hash, navigate]);

  useEffect(() => {
    if (!user) return;
    fetch(`${API}/auth/my-conversations`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setConversations(d.conversations || []))
      .catch(() => {});
  }, [user]);

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});
    navigate("/");
  };

  if (!user) {
    return <PageLoader testid="account-loading" />;
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#050505] text-zinc-100">
      <div className="noise-overlay" aria-hidden="true" />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-40 -top-20 h-[520px] w-[520px] rounded-full opacity-[0.16] blur-[150px]"
        style={{ background: "radial-gradient(circle, #E50914 0%, transparent 65%)" }}
      />
      <Stagger className="relative mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <StaggerItem className="flex items-center justify-between">
          <a href="/" className="font-display text-lg font-extrabold tracking-tight" data-testid="account-logo">
            LUIZ<span className="text-[#E50914]">.DEV</span>
          </a>
          <button
            data-testid="account-logout-btn"
            onClick={logout}
            className="flex items-center gap-2 rounded-full border border-[#2A2A33] px-5 py-2.5 font-mono-code text-xs uppercase tracking-widest text-zinc-300 transition-colors hover:border-[#E50914]/60 hover:text-white"
          >
            <LogOut className="h-4 w-4" />
            Sair
          </button>
        </StaggerItem>

        <StaggerItem className="gradient-ring mt-10 rounded-2xl p-8">
          <div className="flex items-center gap-5">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="h-16 w-16 rounded-full border border-[#E50914]/40" />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full border border-[#E50914]/40 bg-[#E50914]/10">
                <User className="h-7 w-7 text-[#E50914]" />
              </div>
            )}
            <div>
              <h1 className="font-display text-2xl font-bold" data-testid="account-greeting">
                Olá, {user.name || "cliente"}!
              </h1>
              <p className="mt-1 font-mono-code text-xs text-zinc-500">{user.email}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[#E50914]/40 bg-[#E50914]/10 px-3 py-1 font-mono-code text-[10px] uppercase tracking-widest text-[#FF2A3B]">
                  <ShieldCheck className="h-3 w-3" />
                  Conta ativa
                </span>
                <span data-testid="account-secure-badge" className="inline-flex items-center gap-1.5 rounded-full border border-[#22222A] bg-[#050505] px-3 py-1 font-mono-code text-[10px] uppercase tracking-widest text-zinc-500">
                  <Lock className="h-3 w-3 text-emerald-400" />
                  Sessão criptografada
                </span>
              </div>
            </div>
          </div>
        </StaggerItem>

        <StaggerItem className="mt-6 rounded-2xl border border-[#22222A] bg-[#101014]">
          <div className="flex items-center gap-3 border-b border-[#1A1A20] px-8 py-5">
            <MessagesSquare className="h-5 w-5 text-[#E50914]" />
            <div>
              <h2 className="font-display text-lg font-bold">Suas conversas com o assistente</h2>
              <p className="text-xs text-zinc-500">Conversas feitas enquanto você estava logado</p>
            </div>
          </div>
          <div className="p-4" data-testid="account-conversations">
            {conversations.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-zinc-600">
                Nenhuma conversa ainda. Fale com o assistente pela bolinha vermelha no site e ela aparece aqui.
              </p>
            ) : (
              conversations.map((c) => (
                <div
                  key={c.session_id}
                  data-testid={`account-conversation-${c.session_id.slice(0, 8)}`}
                  className="flex items-center gap-4 rounded-xl p-4 transition-colors hover:bg-[#0A0A0C]"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#E50914]/10">
                    <Bot className="h-5 w-5 text-[#E50914]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-zinc-300">"{c.preview}"</p>
                    <span className="mt-1 block font-mono-code text-[10px] text-zinc-600">
                      {c.messages} mensagens · {formatDate(c.last_at)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </StaggerItem>

        <StaggerItem>
          <ProjectTracker />
        </StaggerItem>

        <StaggerItem>
          <PaymentsSection />
        </StaggerItem>

        <StaggerItem>
          <QuoteSection />
        </StaggerItem>

        <StaggerItem>
          <a
            data-testid="account-back-to-site"
            href="/"
            className="mt-8 flex items-center justify-center gap-2 text-xs text-zinc-500 transition-colors hover:text-[#E50914]"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Voltar ao site
          </a>
        </StaggerItem>
      </Stagger>
    </div>
  );
}
